import { useState, useRef, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { Loader2, Upload, X, Sparkles, Save, Square, RectangleHorizontal, RectangleVertical } from 'lucide-react';
import { storyboardApi } from './storyboardApi';

// Grouped optional brief fields. Every field is optional — the AI builds from
// whatever is provided.
const GROUPS = [
  {
    title: 'Project & Goal',
    fields: [
      { key: 'objective', label: 'Goal', ph: 'e.g., Drive pre-orders for the new product line' },
      { key: 'target_audience', label: 'Target audience', ph: 'e.g., Urban professionals 25-40' },
      { key: 'platform', label: 'Platform', ph: 'e.g., TikTok / Reels / YouTube Shorts' },
    ],
  },
  {
    title: 'Concept & Message',
    fields: [
      { key: 'main_idea', label: 'Main video idea', ph: 'The core idea of the video...', textarea: true },
      { key: 'product_service', label: 'Product / service', ph: 'What is being promoted' },
      { key: 'offer_message', label: 'Offer or main message', ph: 'e.g., 20% launch discount' },
      { key: 'call_to_action', label: 'Call to action', ph: 'e.g., Order now at brand.com' },
    ],
  },
  {
    title: 'Brand',
    fields: [
      { key: 'brand_colors', label: 'Brand colors', ph: 'e.g., Matte black, gold accents' },
      { key: 'restrictions', label: 'Important restrictions', ph: 'e.g., No competitor products, no text overlays' },
    ],
    logo: true,
  },
  {
    title: 'Cast & World',
    fields: [
      { key: 'main_characters', label: 'Main characters', ph: 'e.g., Female barista, 30s, short dark hair, denim apron', textarea: true, refKey: 'main_character_image_paths', refLabel: 'Upload main character references' },
      { key: 'secondary_characters', label: 'Secondary characters', ph: 'e.g., Two customers in the background', refKey: 'secondary_character_image_paths', refLabel: 'Upload secondary character references' },
      { key: 'location_environment', label: 'Locations / environment', ph: 'e.g., Industrial loft coffee shop, night city', refKey: 'location_image_paths', refLabel: 'Upload location references' },
    ],
  },
  {
    title: 'Visual Direction',
    fields: [
      { key: 'visual_style', label: 'Visual style', ph: 'e.g., Cinematic, high contrast, editorial' },
      { key: 'camera_style', label: 'Camera style', ph: 'e.g., Handheld close-ups, slow push-ins' },
      { key: 'lighting', label: 'Lighting', ph: 'e.g., Moody, warm practicals, neon rim light' },
      { key: 'emotional_tone', label: 'Mood', ph: 'e.g., Confident, energizing' },
      { key: 'visual_references', label: 'Visual references (described)', ph: 'e.g., Similar to Apple product films', textarea: true },
    ],
    refs: true,
  },
  {
    title: 'Notes',
    fields: [
      { key: 'notes', label: 'Additional notes', ph: 'Anything else the creative director should know...', textarea: true },
    ],
  },
  {
    title: 'Campaign Setup (optional)',
    subtitle: 'Business details the AI uses to tailor the strategy and Campaign Package.',
    fields: [
      { key: 'campaign_type', label: 'Campaign type', ph: 'Pick one or type your own — e.g., Viral, Sales, Branding...', list: ['Viral', 'Sales / Promo', 'Branding', 'Restaurant', 'Product Launch', 'Event', 'Real Estate', 'Lead Generation', 'Local Business'] },
      { key: 'market', label: 'Market / country', ph: 'e.g., USA, Mexico' },
      { key: 'city', label: 'City', ph: 'e.g., Miami, FL' },
      { key: 'campaign_language', label: 'Campaign language', ph: 'e.g., Spanish, English, bilingual' },
      { key: 'website', label: 'Website', ph: 'e.g., brand.com' },
      { key: 'phone', label: 'Phone', ph: 'e.g., +1 (305) 555-0134' },
      { key: 'whatsapp', label: 'WhatsApp', ph: 'e.g., +1 (305) 555-0134' },
      { key: 'address', label: 'Address', ph: 'e.g., 120 Ocean Dr, Miami Beach' },
      { key: 'business_hours', label: 'Business hours', ph: 'e.g., Mon-Sat 9am-8pm' },
      { key: 'service_area', label: 'Service area', ph: 'e.g., Miami-Dade & Broward' },
      { key: 'payment_methods', label: 'Payment methods', ph: 'e.g., Cash, cards, Zelle, Apple Pay' },
    ],
  },
];

const ASPECTS = [
  { id: 'vertical', label: '9:16', Icon: RectangleVertical },
  { id: 'horizontal', label: '16:9', Icon: RectangleHorizontal },
  { id: 'square', label: '1:1', Icon: Square },
];

// Reference-image buckets stored inside the brief JSONB.
const REF_KEYS = [
  'reference_image_paths',
  'main_character_image_paths',
  'secondary_character_image_paths',
  'location_image_paths',
];

export default function BriefForm({ board, logoUrl, authHeader, onBoardChange, onLogoUrlChange, onConceptGenerated }) {
  const { projects } = useApp();
  const [brief, setBrief] = useState(board.brief || {});
  const [title, setTitle] = useState(board.title || '');
  const [projectId, setProjectId] = useState(board.project_id || '');
  const [aspect, setAspect] = useState(board.aspect_ratio || 'horizontal');
  const [logoPath, setLogoPath] = useState(board.logo_storage_path || null);
  const [refImages, setRefImages] = useState(() =>
    Object.fromEntries(REF_KEYS.map(k => [k, (board.brief || {})[k] || []]))
  );
  const [refUrls, setRefUrls] = useState({}); // path -> signed url
  const [saving, setSaving] = useState(false);
  const [generatingConcept, setGeneratingConcept] = useState(false);
  const [uploading, setUploading] = useState(null); // 'logo' | refKey | null
  const [error, setError] = useState('');
  const logoInputRef = useRef(null);
  const refsInputRef = useRef(null);
  const pendingRefKey = useRef('reference_image_paths');

  const setField = (key, value) => setBrief(prev => ({ ...prev, [key]: value }));

  const openRefPicker = (refKey) => {
    pendingRefKey.current = refKey;
    refsInputRef.current?.click();
  };

  const buildPayload = () => ({
    title: title || null,
    project_id: projectId || null,
    aspect_ratio: aspect,
    logo_storage_path: logoPath || '',
    brief: { ...brief, ...refImages },
  });

  const save = async () => {
    setError('');
    setSaving(true);
    const { ok, data } = await storyboardApi.update(board.id, buildPayload(), authHeader);
    setSaving(false);
    if (ok) {
      onBoardChange(data.storyboard);
      return true;
    }
    setError(typeof data?.detail === 'string' ? data.detail : 'Save failed');
    return false;
  };

  const generateConcept = async () => {
    setError('');
    if (!(await save())) return;
    setGeneratingConcept(true);
    const { ok, data } = await storyboardApi.concept(board.id, authHeader);
    if (!ok) {
      setGeneratingConcept(false);
      setError(typeof data?.detail === 'string' ? data.detail : 'Concept generation failed');
      return;
    }
    // Async job (premium reasoning model, 2-6 min): poll the board until ready.
    const deadline = Date.now() + 12 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 6000));
      const res = await storyboardApi.get(board.id, authHeader);
      if (!res.ok) continue;
      const b = res.data.storyboard;
      if (b.status === 'ideating' && b.concept) {
        setGeneratingConcept(false);
        onBoardChange(b);
        onConceptGenerated(b.concept, b.status);
        return;
      }
      if (b.status === 'briefing' && b.error_message) {
        setGeneratingConcept(false);
        setError(b.error_message);
        return;
      }
    }
    setGeneratingConcept(false);
    setError('Concept generation timed out — try again.');
  };

  const uploadFile = async (file, kind) => {
    setError('');
    setUploading(kind);
    const { ok, data } = await storyboardApi.uploadImage(file, authHeader);
    setUploading(null);
    if (!ok || !data.file) {
      setError(typeof data?.detail === 'string' ? data.detail : 'Upload failed');
      return;
    }
    const path = data.file.storage_path || data.file.id;
    if (kind === 'logo') {
      setLogoPath(path);
      onLogoUrlChange(data.file.url || null);
    } else {
      setRefImages(prev => ({ ...prev, [kind]: [...(prev[kind] || []), path] }));
      setRefUrls(prev => ({ ...prev, [path]: data.file.url }));
    }
  };

  useEffect(() => {
    Object.values(refImages).flat().forEach(async (path) => {
      if (refUrls[path]) return;
      const { ok, data } = await storyboardApi.signedUrl('project-files', path);
      if (ok && data.url) setRefUrls(prev => ({ ...prev, [path]: data.url }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refImages]);

  const removeRef = (refKey, path) =>
    setRefImages(prev => ({ ...prev, [refKey]: (prev[refKey] || []).filter(x => x !== path) }));

  const renderRefUploader = (refKey, label, testId) => (
    <div className="pt-1.5 space-y-2">
      <button
        data-testid={testId}
        onClick={() => openRefPicker(refKey)}
        disabled={uploading === refKey}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/60 border border-gray-200/60 text-gray-700 hover:bg-white/80 transition-all disabled:opacity-50"
      >
        {uploading === refKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        <span>{label}</span>
      </button>
      {(refImages[refKey] || []).length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {refImages[refKey].map(p => (
            <div key={p} className="relative">
              {refUrls[p]
                ? <img src={refUrls[p]} alt="ref" className="h-14 w-14 rounded-lg object-cover border border-gray-200/60" />
                : <div className="h-14 w-14 rounded-lg bg-gray-100 animate-pulse" />}
              <button
                data-testid={`${testId}-remove`}
                onClick={() => removeRef(refKey, p)}
                className="absolute -top-1.5 -right-1.5 bg-gray-900 text-white rounded-full p-0.5"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const inputCls = 'w-full px-3 py-2.5 rounded-xl bg-white/40 border border-gray-200/40 text-sm text-gray-700 outline-none focus:border-gray-300 placeholder:text-gray-400';

  return (
    <div className="space-y-5" data-testid="sb-brief-form">
      {/* Title + project + aspect */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Project / Client</label>
          <input
            data-testid="sb-brief-title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g., ColdBrew Co. — Launch"
            className={inputCls}
          />
        </div>
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Link to project</label>
          <select
            data-testid="sb-brief-project"
            value={projectId}
            onChange={e => setProjectId(e.target.value)}
            className={inputCls}
          >
            <option value="" label="No project" />
            {projects.map(p => <option key={p.id} value={p.id} label={p.name} />)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Format</label>
          <div className="flex items-center gap-1.5">
            {ASPECTS.map(({ id, label, Icon }) => (
              <button
                key={id}
                data-testid={`sb-aspect-${id}`}
                onClick={() => setAspect(id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  aspect === id ? 'bg-gray-900 text-white' : 'bg-white/40 border border-gray-200/40 text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grouped fields */}
      {GROUPS.map(group => (
        <div key={group.title} className="rounded-2xl border border-gray-200/50 bg-white/30 p-4 space-y-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{group.title}</p>
          {group.subtitle && <p className="text-[11px] text-gray-400 -mt-1">{group.subtitle}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {group.fields.map(f => (
              <div key={f.key} className={f.textarea ? 'sm:col-span-2' : ''}>
                <label className="text-xs text-gray-400 mb-1 block">{f.label}</label>
                {f.textarea ? (
                  <textarea
                    data-testid={`sb-field-${f.key}`}
                    value={brief[f.key] || ''}
                    onChange={e => setField(f.key, e.target.value)}
                    placeholder={f.ph}
                    className={`${inputCls} resize-none min-h-[64px]`}
                  />
                ) : (
                  <>
                    <input
                      data-testid={`sb-field-${f.key}`}
                      value={brief[f.key] || ''}
                      onChange={e => setField(f.key, e.target.value)}
                      placeholder={f.ph}
                      className={inputCls}
                      list={f.list ? `sb-list-${f.key}` : undefined}
                    />
                    {f.list && (
                      <datalist id={`sb-list-${f.key}`}>
                        {f.list.map(opt => <option key={opt} value={opt} />)}
                      </datalist>
                    )}
                  </>
                )}
                {f.refKey && renderRefUploader(f.refKey, f.refLabel, `sb-ref-upload-${f.key}`)}
              </div>
            ))}
          </div>

          {group.logo && (
            <div className="flex items-center gap-3 pt-1">
              <button
                data-testid="sb-logo-upload"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploading === 'logo'}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/60 border border-gray-200/60 text-gray-700 hover:bg-white/80 transition-all disabled:opacity-50"
              >
                {uploading === 'logo' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                <span>{logoPath ? 'Replace logo' : 'Upload logo (optional)'}</span>
              </button>
              <input ref={logoInputRef} type="file" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" className="hidden"
                onChange={e => { if (e.target.files?.[0]) uploadFile(e.target.files[0], 'logo'); e.target.value = ''; }} />
              {logoPath && (
                <div className="relative">
                  {logoUrl
                    ? <img src={logoUrl} alt="logo" className="h-10 w-10 rounded-lg object-contain bg-white border border-gray-200/60" data-testid="sb-logo-thumb" />
                    : <div className="h-10 w-10 rounded-lg bg-gray-100" />}
                  <button
                    data-testid="sb-logo-remove"
                    onClick={() => { setLogoPath(null); onLogoUrlChange(null); }}
                    className="absolute -top-1.5 -right-1.5 bg-gray-900 text-white rounded-full p-0.5"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              )}
              <p className="text-[10px] text-gray-400">Logo appears in max 1-3 closing scenes only.</p>
            </div>
          )}

          {group.refs && renderRefUploader('reference_image_paths', 'Upload reference images (optional)', 'sb-refs-upload')}
        </div>
      ))}

      {/* Shared hidden input for all reference uploads (target set by pendingRefKey) */}
      <input
        ref={refsInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
        multiple
        className="hidden"
        onChange={e => {
          Array.from(e.target.files || []).forEach(f => uploadFile(f, pendingRefKey.current));
          e.target.value = '';
        }}
      />

      {error && <p className="text-xs text-red-500" data-testid="sb-brief-error">{error}</p>}

      {/* Actions */}
      <div className="flex items-center gap-2.5 pb-4">
        <button
          data-testid="sb-save-brief"
          onClick={save}
          disabled={saving || generatingConcept}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-medium bg-white/60 border border-gray-200/60 text-gray-700 hover:bg-white/80 transition-all disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save brief
        </button>
        <button
          data-testid="sb-generate-concept"
          onClick={generateConcept}
          disabled={saving || generatingConcept}
          className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 transition-all active:scale-[0.98] disabled:opacity-50"
        >
          {generatingConcept ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          <span>{generatingConcept ? 'Directing...' : board.concept ? 'Re-generate concept' : 'Generate creative concept'}</span>
        </button>
        {generatingConcept && (
          <span className="text-[11px] text-gray-400">Strategic analysis + creative concept in progress (deep reasoning, 2-6 min)...</span>
        )}
      </div>
    </div>
  );
}
